/**
 * The tenant's detection categories (sc-11031), shared app-wide.
 *
 * Categories are tenant-editable, so an entity type's label and color come from
 * here, not a hard-coded table. A tiny module store keeps them: components call
 * `useDetectionCategories()` (which loads them once and re-renders on change) and
 * plain helpers call `getEntityTypeConfig(key)`.
 *
 * Lookup order: the tenant's category → a system type (OTHER, CUSTOM, DICTIONARY)
 * → "Other" for a key whose category was deleted. Until the list has loaded,
 * built-in types fall back to their static look so nothing flashes unlabeled.
 */
import { useEffect, useSyncExternalStore } from 'react';
import { ENTITY_TYPE_CONFIG, type BuiltInEntityType, type DetectionCategory } from '../types';
import { categoriesApi } from './api';

export interface EntityTypeDisplay {
  label: string;
  color: string;
  icon: string;
}

/** Who may add or edit categories. Mirrors CATEGORY_WRITE_ROLES on the server's
 *  /categories router (sc-11031). */
export const CATEGORY_EDIT_ROLES = ['redactit_admin', 'redactit_editor', 'darwin', 'darwin_support'];

/** Keys the system assigns itself; never a tenant category. */
export const SYSTEM_ENTITY_TYPES = ['OTHER', 'CUSTOM', 'DICTIONARY'] as const;

/** How a finding of a deleted (or unknown) category is shown. */
export const OTHER_DISPLAY: EntityTypeDisplay = { label: 'Other', color: '#94A3B8', icon: 'tag' };

let categories: DetectionCategory[] | null = null;
let byKey = new Map<string, DetectionCategory>();
let version = 0;
let loading: Promise<void> | null = null;
const listeners = new Set<() => void>();

function emit() {
  version += 1;
  listeners.forEach((listener) => listener());
}

/** Replace the store's list (after a load or an edit in Settings). */
export function setDetectionCategories(next: DetectionCategory[] | null): void {
  categories = next;
  if (next === null) loading = null; // cleared: the next use loads again
  byKey = new Map((next ?? []).map((c) => [c.key, c]));
  emit();
}

export function getDetectionCategories(): DetectionCategory[] | null {
  return categories;
}

/** Load the tenant's categories once (later calls reuse the first request). */
export function loadDetectionCategories(force = false): Promise<void> {
  if (loading && !force) return loading;
  loading = categoriesApi
    .list()
    .then((list) => setDetectionCategories(list))
    .catch((err) => {
      loading = null; // let a later mount retry
      console.error('Failed to load detection categories:', err);
    });
  return loading;
}

function staticDisplay(key: string): EntityTypeDisplay | undefined {
  return ENTITY_TYPE_CONFIG[key as BuiltInEntityType];
}

/** Label, color and icon for an entity type key. Never undefined. */
export function getEntityTypeConfig(key: string | null | undefined): EntityTypeDisplay {
  if (!key) return OTHER_DISPLAY;
  const category = byKey.get(key);
  if (category) {
    return { label: category.label, color: category.color, icon: staticDisplay(key)?.icon ?? 'tag' };
  }
  if ((SYSTEM_ENTITY_TYPES as readonly string[]).includes(key)) {
    return staticDisplay(key) ?? OTHER_DISPLAY;
  }
  if (categories === null) {
    return staticDisplay(key) ?? OTHER_DISPLAY; // not loaded yet
  }
  return OTHER_DISPLAY; // the category was deleted
}

/** The tenant's enabled categories, for the type pickers (empty until loaded). */
export function getEnabledCategories(): DetectionCategory[] {
  return (categories ?? []).filter((c) => c.enabled);
}

/** The quick-pick list before the tenant's categories load (the old fixed one). */
const FALLBACK_PICKER_TYPES = [
  'PERSON', 'DATE', 'SSN', 'PHONE', 'EMAIL', 'ADDRESS',
  'DRIVERS_LICENSE', 'CASE_NUMBER', 'ORG', 'MEDICAL',
  'GOV_EMPLOYEE_ID', 'MONEY', 'CUSTOM',
];

/** Types the manual-add and change-type pickers offer: the tenant's enabled
 *  categories, then Custom. */
export function pickerEntityTypes(): string[] {
  if (categories === null) return FALLBACK_PICKER_TYPES;
  return [...getEnabledCategories().map((c) => c.key), 'CUSTOM'];
}

/** A type picker's types: pickerEntityTypes, with the current type first when it
 *  isn't one of them (e.g. Other PII, or a rarely-used NER type). */
export function entityTypeChoices(currentType: string): string[] {
  const types = pickerEntityTypes();
  return types.includes(currentType) ? types : [currentType, ...types];
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Subscribe a component to the categories: loads them on first use and
 * re-renders it whenever they change. Returns the list (null until loaded).
 */
export function useDetectionCategories(): DetectionCategory[] | null {
  useEffect(() => {
    void loadDetectionCategories();
  }, []);
  useSyncExternalStore(subscribe, () => version);
  return categories;
}

// Mirrors the server's pattern limits (server/models/detection_category.py).
const PATTERNS_MAX = 10;
const PATTERN_MAX_LENGTH = 300;

/** One regular expression per non-blank line. */
export function parsePatterns(text: string): string[] {
  return text.split('\n').map((p) => p.trim()).filter(Boolean);
}

/** First problem with the patterns, checked the way the server will (it uses
 *  Python's `re`; JavaScript's RegExp is close enough to catch typos). */
export function patternsError(text: string): string | null {
  const patterns = parsePatterns(text);
  if (patterns.length > PATTERNS_MAX) return `At most ${PATTERNS_MAX} patterns`;
  for (const pattern of patterns) {
    if (pattern.length > PATTERN_MAX_LENGTH) return `A pattern can be at most ${PATTERN_MAX_LENGTH} characters`;
    try {
      new RegExp(pattern);
    } catch {
      return `Invalid pattern: ${pattern}`;
    }
  }
  return null;
}

/** The learned-values lists to send on save: only those the form changed, so an
 *  edit doesn't overwrite values learned while the dialog was open. */
export function learnedChanges(
  form: { examples: string[]; counter_examples: string[] },
  original: DetectionCategory | undefined,
): { examples?: string[]; counter_examples?: string[] } {
  const changes: { examples?: string[]; counter_examples?: string[] } = {};
  if (!original) return changes;
  if (form.examples.length !== (original.examples ?? []).length) changes.examples = form.examples;
  if (form.counter_examples.length !== (original.counter_examples ?? []).length) {
    changes.counter_examples = form.counter_examples;
  }
  return changes;
}

/** 'Learned from reviews' line for a category row, or null when it has none. */
export function learnedSummary(category: Pick<DetectionCategory, 'examples' | 'counter_examples'>): string | null {
  const examples = category.examples?.length ?? 0;
  const counter = category.counter_examples?.length ?? 0;
  if (!examples && !counter) return null;
  const parts = [];
  if (examples) parts.push(`${examples} ${examples === 1 ? 'example' : 'examples'}`);
  if (counter) parts.push(`${counter} ${counter === 1 ? 'counter-example' : 'counter-examples'}`);
  return `Learned from reviews: ${parts.join(', ')}`;
}
